/**
 * What okthx may use, in the words the user is asked to agree to.
 *
 * Shared because both clients now ask the same question. Web asks it on a page
 * the OAuth tab is parked on; the iOS app asks it before the flow starts, in a
 * screen of its own. The wording and the id list must not drift between them: a
 * user who ticks "Calendar" on one platform and on the other has granted the
 * same thing, and the api turns the same ids back into scopes for both.
 *
 * Icons stay with each client. They are the one part that cannot be shared —
 * web draws lucide-react, the app draws lucide-react-native — and they carry no
 * meaning the api reads.
 */

import type { AuthFlow } from "@/types/auth.ts";

/** One piece of access the user can decline. */
export type AccessItem = {
  /**
   * Matches `EXPLAINABLE_GOOGLE_SCOPES` / `EXPLAINABLE_MICROSOFT_SCOPES` in the
   * api, which turn these ids back into provider scopes. An id neither table
   * carries contributes nothing and silently grants nothing.
   */
  id: string;
  title: string;
  /** Says what okthx does with the access, not which scope it maps to. */
  description: string;
  /**
   * Replaces {@link AccessItem.description} for the providers named here.
   *
   * Reached for only when a provider's scopes cannot do what the shared
   * sentence promises — not for tone. The same words in front of two users who
   * are granting different things is how a consent screen overstates itself,
   * and the shared list exists to stop exactly that.
   */
  descriptionByProvider?: Record<string, string>;
};

/**
 * Described by what okthx does with the access rather than by the scope behind
 * it. A user deciding whether to hand over their mailbox is answering a
 * question about their mail, not about `gmail.modify`.
 *
 * One item per question the user can actually answer, which is not the same as
 * one item per provider scope. `contacts` costs two scopes at Google — the
 * user's own saved contacts and their organization's directory — because the
 * seam between those is Google's filing system rather than anything the user is
 * deciding. Split in two, the choice could only be made by someone who already
 * knew Google files a colleague somewhere other than a contact.
 *
 * MERGING RAISES THE BAR ON THE COPY. One tick now buys the directory, which
 * reaches people the user has never corresponded with and who are not party to
 * this consent, so the description has to name it. "Anyone in your organization's
 * directory" rather than "colleagues": the scope returns every profile in the
 * domain, and colleagues reads as the handful someone works with. An item whose
 * words cover less than its scopes is the failure this list exists to prevent.
 *
 * The same rule cuts the other way for Microsoft, which is why `contacts` is
 * the one item with per-provider copy. Graph has no directory scope, so
 * Microsoft's `contacts` buys the user's own address book and nothing else.
 * Shared wording that named the directory would promise an Outlook user access
 * okthx cannot ask for, and that is the same failure with the sign reversed.
 */
export const ACCESS_ITEMS: readonly AccessItem[] = [
  {
    id: "email",
    title: "Email",
    description: "Triage your inbox, label what matters, and draft replies in your voice.",
  },
  {
    id: "calendar",
    title: "Calendar",
    description: "See what your day holds, and book, move, and cancel events for you.",
  },
  {
    id: "contacts",
    title: "Contacts",
    description:
      "Look up the people you email and meet with, plus anyone in your organization's directory, so you can name them instead of typing emails or phone numbers.",
    descriptionByProvider: {
      microsoft:
        "Look up the people you email and meet with, so you can name them instead of typing emails or phone numbers.",
    },
  },
];

/**
 * Which access ids each provider can actually honour, in display order.
 *
 * The two lists agree today, and the table stays because agreeing is not the
 * same as being one list. An id offered by a provider that carries no scope for
 * it would let a user tick something that maps to nothing — the api drops ids it
 * does not recognise, and the user would be left believing they had granted it.
 *
 * What each id COSTS in provider scopes is the api's business, not this table's:
 * `contacts` is two scopes at Google and one at Microsoft, and no screen has a
 * reason to know that.
 */
export const PROVIDER_ACCESS_IDS: Record<string, readonly string[]> = {
  google: ["email", "calendar", "contacts"],
  microsoft: ["email", "calendar", "contacts"],
};

export const PROVIDER_NAMES: Record<string, string> = {
  google: "Google",
  microsoft: "Microsoft",
};

/**
 * Provider and flow to the start route that opens the consent screen.
 *
 * Keyed on both because the two routes do different things to the caller's
 * session: `login` signs the user in AS the account they pick, `add_account`
 * adds it under the user they already are.
 */
export const PROVIDER_START_PATHS: Record<string, Record<AuthFlow, string>> = {
  google: {
    login: "/api/p/auth/google/base",
    add_account: "/api/a/auth/google/add-account",
  },
  microsoft: {
    login: "/api/p/auth/microsoft/base",
    add_account: "/api/a/auth/microsoft/add-account",
  },
};

/**
 * The provider's own entries, falling back to Google's for an unrecognised name
 * so a screen never renders an empty form or a dead link.
 *
 * Google is the fallback because it is the original provider and its list is
 * the widest — the same choice the api's start routes make.
 */
export const providerAccessIds = (provider: string) =>
  PROVIDER_ACCESS_IDS[provider] ?? PROVIDER_ACCESS_IDS["google"] ?? [];

export const providerStartPath = (provider: string, flow: AuthFlow = "login") => {
  const routes = PROVIDER_START_PATHS[provider] ?? PROVIDER_START_PATHS["google"];
  return routes?.[flow] ?? "";
};

export const providerName = (provider: string) =>
  PROVIDER_NAMES[provider] ?? "your provider";

/**
 * The items this provider can honour, with their copy, in display order.
 *
 * Resolves `descriptionByProvider` here rather than at each screen, so a caller
 * that reads `item.description` gets wording this provider's scopes can back.
 */
export const accessItemsFor = (provider: string): readonly AccessItem[] => {
  const ids = providerAccessIds(provider);
  return ACCESS_ITEMS.filter((item) => ids.includes(item.id)).map((item) => {
    const override = item.descriptionByProvider?.[provider];
    return override === undefined ? item : { ...item, description: override };
  });
};

/**
 * The `access` parameter for a chosen set of ids.
 *
 * Always a string, even when nothing is chosen: the api reads an ABSENT
 * `access` as "this request never came from the screen" and would ask for
 * everything, which is the opposite of what an empty selection means.
 */
export const accessParam = (selectedIds: readonly string[]) => selectedIds.join(",");

export const ACCESS_CHOICE_TITLE = "Choose what Okthx can use";
export const ACCESS_CHOICE_INTRO =
  "Okthx does more with more access, and works without any of it. Pick what you are comfortable with now — you can change it later in Settings.";

/**
 * The connector prompt's own words for the same screen. Sign-up is the first
 * ask and says the product works without access; the prompt comes back to a
 * user who has seen it, and says what connecting gets them.
 */
export const CONNECTOR_PROMPT_ACCESS_INTRO =
  "Connect your email, calendar and contacts, and Okthx can assist where you already do your work.";

/**
 * The prompt's items: sign-up's, with a shorter Contacts line. Unlike sign-up's
 * Google wording it does not name the organization's directory; a product
 * decision for the prompt (BIG-9222), where the user has already been through
 * the fuller wording at sign-in.
 */
export function connectorPromptAccessItems(items: readonly AccessItem[]): AccessItem[] {
  return items.map((item) =>
    item.id === "contacts"
      ? {
          ...item,
          description: "Mention your contacts by name instead of typing emails or phone numbers.",
        }
      : item,
  );
}

/** The line under Continue: what the provider's consent will ask for. */
export function accessChoiceFootnote(providerName: string, selectedCount: number): string {
  return selectedCount === 0
    ? `${providerName} will only confirm who you are. Okthx will ask you for anything it needs.`
    : `${providerName} will ask you to confirm this.`;
}
